import React, { useState, useEffect, useCallback } from 'react';
import { Button, Chip, Container, Dialog, Grid, Paper, Stack, Typography, Checkbox, Box } from '@mui/material';
import { FormProvider, SelectTag, TextField } from '../../../../../components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, BASE_URL } from '../../../../../utils';
import { useLocation } from 'react-router-dom';
import { OPD_PROCEDURE_REPORT_TYPE } from '../../../../../constants/dropdown';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { toast } from 'react-toastify';


export const DoctorWiseOpdProcedureReport = () => {
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [sponsorDropdown, setSponsorDropdown] = useState([]);
  const [doctorDropdown, setDoctorDropdown] = useState([]);
  const [referralDropdown, setReferralDropdown] = useState([]);
  const [openPrint, setOpenPrint] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState([]);

  const schema = Yup.object().shape({
    from_date: Yup.string().required(' '),
    to_date: Yup.string().required(' ')
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      from_date: new Date().toISOString().split('T')[0],
      to_date: new Date().toISOString().split('T')[0],
      sponsor: []
    },
    mode: 'all'
  });

  const { watch, control, setValue } = methods;

  const getSponsorDropdown = useCallback(async () => {
    try {
      const data = await dropdownFetch('sponsor');
      setSponsorDropdown(data);
    } catch (error) {
      console.error('Error fetching sponsor data:', error);
    }
  }, []);

  const getDoctorDropdown = useCallback(async () => {
    try {
      const data = await dropdownFetch('doctor');
      setDoctorDropdown(data);
    } catch (error) {
      console.error('Error fetching Doctor data:', error);
    }
  }, []);

  const getReferralDropdown = useCallback(async () => {
    const data = await dropdownFetch('referral');
    if (!selectedRecord) {
      const [defaultReferal] = data.filter(({ status }) => status === 'default');
      setValue('ref_by', defaultReferal?._id);
    }
    setReferralDropdown(data);
    console.log('Hey', referralDropdown);
  }, []);

  useEffect(
    () => {
      getSponsorDropdown();
      getDoctorDropdown();
      getReferralDropdown();
    },
    [getSponsorDropdown],
    [getDoctorDropdown],
    [getReferralDropdown]
  );

  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
  const handleExcelReport = async () => {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';
    
    // Fetch the data
    const response = await axios.get(`${BASE_URL}/print/hospital/outpatient-management/doctor-wise-opd-procedure-reports?refId=${JSON.stringify({doctor: watch('doctor'), from_date: watch('from_date'), to_date: watch('to_date')})}`);
    
    if (response.data.success) {
      const opdData = response?.data?.data?.opdData?.data || [];
      const opdBillingData = response?.data?.data?.opdBillingData?.data || [];
      
      // Check if there is data to export
      if (opdData.length > 0 || opdBillingData.length > 0) {
        console.log("OPD Data:", opdData);
        console.log("OPD Billing Data:", opdBillingData);
  
        const fileName = `Consultant_Wise_Opd_Report_${JSON.stringify(watch('from_date'))}_to_${JSON.stringify(watch('to_date'))}`;
  
        // Format OPD Data
        const formattedOpdData = opdData.map((item, index) => ({
          "Sr. No.": index + 1,
          'Bill Date': formatDate(item?.date),
          'Type': "OPD",
          'Patient Name': `${item?.name_suffix} ${item?.first_name} ${item?.last_name}`,
          'Uhid No.': item?.uhid,
          'Payment Mode': item?.payment_mode,
          'Consultant': item?.consultant.name,
          'Services': "OPD Registration",
          'Panel': item?.sponsor?.name,
          'Recd. Amt.': item?.recd,
          'Ref By': item?.refby.referral_name,
        }));
  
        // Format OPD Billing Data
        const formattedOpdBillingData = opdBillingData.map((item, index) => ({
          "Sr. No.": index + 1,
          'Bill Date': formatDate(item?.date),
          'Type': "OPD Billing",
          'Patient Name': item?.patient?.first_name + ' ' + item?.patient?.last_name,
          'Uhid No.': item?.uhid,
          'Payment Mode': item?.pay_mode,
          'Consultant': item?.patient?.consultant?.name,
          'Services': item?.combinedServiceDetails?.map(service => service.service_name).join(", "),
          'Panel': item?.patient?.sponsor?.name,
          'Recd. Amt.': item?.total_recd,
        }));
  
        // Combine both OPD Data and Billing Data
        const combinedData = [...formattedOpdData, ...formattedOpdBillingData];
  
        // Create the Excel worksheet
        const ws = XLSX.utils.json_to_sheet(combinedData);
        
        // Adjust column width - setting width of the "Services" column to be larger
        ws['!cols'] = [
          { wch: 10 }, // Sr. No.
          { wch: 12 }, // Bill Date
          { wch: 10 }, // Type
          { wch: 20 }, // Patient Name
          { wch: 12 }, // Uhid No.
          { wch: 12 }, // Payment Mode
          { wch: 20 }, // Consultant
          { wch: 25 }, // Services (increased width here)
          { wch: 20 }, // Panel
          { wch: 15 }, // Recd. Amt.
          { wch: 15 }, // Ref By
        ];
  
        const wb = { Sheets: { 'data': ws }, SheetNames: ['data'] };
  
        // Generate Excel file and save it
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: fileType });
        saveAs(data, fileName + fileExtension);
      } else {
        toast(`No Data Found For These Filters`, { variant: 'error' });
      }
    } else {
      toast(`Something Went Wrong`, { variant: 'error' });
    }
  };
  
  

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Doctor Wise Opd Procedure Report
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <Button
                  variant="contained"
                  id={`${lastPathSegment}Print`}
                  disabled={!watch('to_date')}
                  onClick={() => window.open(`/print${pathname}?refId=${JSON.stringify({doctor: watch('doctor'),refby:watch('ref_by'),sponsor:watch('sponsor'), from_date: watch('from_date'), to_date: watch('to_date')})}`,'_blank' ) } >
                  View Pdf Report
                </Button>
                <Button
                  variant="contained"
                  id={`${lastPathSegment}Print`}
                  disabled={!watch('to_date')}
                  onClick={handleExcelReport} >
                  View Excel Report
                </Button>
                <Button variant="contained">Exit</Button>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '60%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={11} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Doctor</Typography>
                    <SelectTag formControlSX={{ flex: 0.8 }} name="doctor" menus={[{ name: 'All', _id: 'all'}, ...doctorDropdown]} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Ref By</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="ref_by"
                      menus={[
                        { name: 'All', _id: 'all' }, 
                        ...referralDropdown.map((data) => ({ ...data, name: data.referral_name }))
                      ]}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Sponsor</Typography>
                    <SelectTag formControlSX={{ flex: 0.8 }} name="sponsor" menus={[
                      { name: 'All', _id: 'all'},
                      ...sponsorDropdown 
                    ]
                    } />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>From Date</Typography>
                    <TextField sx={{ flex: 0.3 }} name="from_date" type="date" />
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>To Date</Typography>
                    <TextField sx={{ flex: 0.3 }} name="to_date" type="date" />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openPrint}
        onClose={() => setOpenPrint(false)}
        PaperProps={{ sx: { width: '80%', height: '85%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      ></Dialog>
    </>
  );
};
